import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';
import { iaRa } from 'lib/helpers/utility';
import { useMedication } from 'lib/hooks/queries/medication/useMedication';

import { DEFAULT_FILTERS } from 'components/Patient/medications/configs';
import MedicationsTable from 'components/practice/charts/Overview/widgets/components/MedicationsTable';
import MedicationWidgetFooter from 'components/practice/charts/Overview/widgets/lib/MedicationWidgetFooter';
import medicationsCols from 'components/practice/charts/Overview/widgets/lib/medicationsCols';
import { currentPractice } from 'components/practice/practiceState';
import AgTablePage from 'components/shared/AgTablePage/AgTablePage';
import UpsertProviderMedication from 'components/shared/Forms/Custom/ProviderMedications/components/UpsertMedication';
import {
  getRowId,
  onRowClicked
} from 'components/shared/Forms/Custom/ProviderMedications/lib/medicationTableSelectHelpers';
import Modal from 'components/shared/Modal/Modal';
import Allowed from 'components/shared/Permissions/Allowed';
import { permissions } from 'components/state';

const formatMedicationsData = (data) => {
  return iaRa(data?.medicationHistory?.response?.medications);
};

const PatientHistoryMedications = () => {
  const userPermissions = useRecoilValue(permissions);
  const practice = useRecoilValue(currentPractice);
  const [itemModal, setItemModal] = useState({ open: false, data: null });
  return (
    <Allowed requiredPermissions="patients_medical_history.read">
      <TableContextProvider name="Medications" defaultFilters={DEFAULT_FILTERS}>
        <Medications
          itemModal={itemModal}
          setItemModal={setItemModal}
          userPermissions={userPermissions}
          practice={practice}
        />
      </TableContextProvider>
    </Allowed>
  );
};

const Medications = ({ setItemModal, itemModal, userPermissions, practice }) => {
  const { patient } = useOutletContext();
  const patientId = patient?.id;

  const queryParams = useMemo(
    () => ({
      patientId
    }),
    [patient?.id]
  );

  const [show, setShow] = useState(false);
  const { selectedRows, gridApi, setColDefs } = useTableContext();

  const formattedData = iaRa(selectedRows)?.map((row) => {
    const { data } = row;
    return data;
  });
  const { data } = useMedication({
    params: { patientId },
    dependencies: [String(patientId)],
    options: {
      enabled: !!patientId,
      select: (response) => iaRa(response?.medicationHistory?.response?.medications)
    }
  });

  useEffect(() => {
    if (!gridApi) return;

    setColDefs(
      medicationsCols({
        setShowMedicationModal: setItemModal,
        userPermissions,
        practice,
        showCheckboxes: true,
        gridApi
      })
    );
  }, [gridApi]);

  const onCellClicked = async (e) => {
    if (['0', 'actions'].includes(e?.column?.colId)) return;

    if (e?.event?.target?.closest('.no-row-select')) return;

    if (e?.node?.group) {
      return;
    }

    await onRowClicked({ e, gridApi });
  };

  return (
    <>
      <AgTablePage
        noTitle
        noNewButton
        noDisplayButton
        patientId={patient?.id}
        name="patient-history-medications"
        queryKey="direct.format"
        defaultFilters={DEFAULT_FILTERS}
        defaultColumns={medicationsCols({
          setShowMedicationModal: setItemModal,
          userPermissions,
          practice,
          showCheckboxes: false
        })}
        // rowStyle={{ cursor: 'pointer' }}
        useData={useMedication}
        queryParams={queryParams}
        queryOptions={{ enabled: !!patientId }}
        dependencies={[patient?.id]}
        formatData={formatMedicationsData}
      />
      <Modal
        handleOpen={show}
        className="w-[91%] bg-white sm:w-[96%] md:w-[96%] lg:w-[96%] xxl:max-w-[1920px]"
        bodyClassName="p-0"
        title="Medications"
        footer={
          <MedicationWidgetFooter
            gridApi={gridApi}
            formattedData={formattedData}
            setShow={setShow}
          />
        }
        handleClose={() => setShow(false)}>
        <MedicationsTable data={data} onCellClicked={onCellClicked} getRowId={getRowId} />
      </Modal>

      {itemModal?.open && (
        <UpsertProviderMedication showModal={itemModal} hideModal={() => setItemModal(null)} />
      )}
    </>
  );
};

export default PatientHistoryMedications;
